/** The Notification Feature Flags grid search namespaces.
 *
 * Operators per namespace are a SUBSET of the spec's `x-namespace-operators`
 * on NotificationFeatureFlagFilterNode (contract-tested). `exact` is
 * deliberately omitted wherever `iexact` exists (tenant/ota/updated_by) — the
 * DSL serialises both to `=`, which re-parses to `iexact`, so an `exact` chip
 * couldn't round-trip; ID pinning goes through the backend's all-digits
 * fallback. `tour`/`notification`/`id` are exact-only integers, which
 * round-trip fine.
 *
 * `feature` suggestions come from the (small, code-defined) registry passed in
 * by the page — no API round-trip per keystroke.
 *
 * `tag` (icontains) matches level=tag rows by their chip contents — concrete
 * tag name, category code, or stored spec strings.
 */
import type { NamespaceConfigMap } from '@/components/QuerySearch';

import type { NotificationFeatureDef } from '@/api/notificationFeatures';
import { makeSuggestionFetcher } from '@/api/notifications';
import * as otasApi from '@/api/otas';

export const LEVEL_LABELS = ['system', 'ota', 'tenant', 'tour', 'notification', 'tag'];

/** Build a local (no-API) suggestion fetcher from a fixed label list. */
function localLabels(labels: string[]) {
  return (query: string) =>
    Promise.resolve(
      labels
        .filter((s) => s.toLowerCase().includes(query.toLowerCase()))
        .map((s) => ({ value: s, label: s })),
    );
}

function featureSuggestions(defs: NotificationFeatureDef[]) {
  return (query: string) =>
    Promise.resolve(
      defs
        .filter(
          (f) =>
            f.codename.toLowerCase().includes(query.toLowerCase()) ||
            f.name.toLowerCase().includes(query.toLowerCase()),
        )
        .map((f) => ({ value: f.codename, label: `${f.codename} — ${f.name}` })),
    );
}

async function otaSuggestions(query: string) {
  const page = await otasApi.list({ search: query, pageSize: 20 });
  return page.results.map((o) => ({ value: o.name || String(o.id), label: o.name || `#${o.id}` }));
}

export function buildFeatureFlagNamespaces(defs: NotificationFeatureDef[]): NamespaceConfigMap {
  return {
    feature: {
      label: 'feature',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: featureSuggestions(defs),
      help: 'Feature codename from the registry.',
      examples: ['feature=pickup', 'feature=marketing_cta'],
    },
    level: {
      label: 'level',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(LEVEL_LABELS),
      help: 'Flag level: system, ota, tenant, tour, notification or tag.',
      examples: ['level=tenant'],
    },
    tenant: {
      label: 'tenant',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tenant'),
      help: 'Tenant-level flags for this operator (by code; an all-digits value also matches the tenant id).',
      examples: ['tenant=curatedplanet'],
    },
    ota: {
      label: 'ota',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: otaSuggestions,
      help: 'OTA-level flags for this agency (by name; an all-digits value also matches the id).',
      examples: ['ota=Viator'],
    },
    tour: {
      label: 'tour',
      operators: ['exact'],
      valueType: 'integer',
      help: 'Tour-level flags for this tour id.',
      examples: ['tour=8123'],
    },
    notification: {
      label: 'notification',
      operators: ['exact'],
      valueType: 'integer',
      help: 'Notification-level flags for this notification id.',
      examples: ['notification=3227'],
    },
    tag: {
      label: 'tag',
      operators: ['icontains'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tag'),
      help: 'Tag-condition rows whose chips mention this text (tag name, category code, or stored spec).',
      examples: ['tag=~vip'],
    },
    enabled: {
      label: 'enabled',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(['yes', 'no']),
      help: 'Flag value — yes/no. (Absence of a row means enabled by default.)',
      examples: ['enabled=no'],
    },
    comment: {
      label: 'comment',
      operators: ['icontains'],
      valueType: 'string',
      help: 'Comment substring.',
      examples: ['comment=~onboarding'],
    },
    updated_by: {
      label: 'updated_by',
      operators: ['iexact'],
      valueType: 'string',
      help: 'Last editor, by email (an id goes through the backend exact operator).',
      examples: ['updated_by=ops@curatedplanet.com'],
    },
    created: {
      label: 'created',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the flag row was created, UTC. `=` matches a whole day; a range takes date-time bounds.',
      examples: ['created=2026-07-29'],
    },
    updated: {
      label: 'updated',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the flag row was last changed, UTC. `=` matches a whole day; a range takes date-time bounds.',
      examples: ['updated="2026-07-01 09:00".."2026-07-24 18:00"'],
    },
    id: {
      label: 'id',
      operators: ['exact'],
      valueType: 'integer',
      help: 'Flag row id.',
      examples: ['id=42'],
    },
  };
}
